import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../contexts/AuthContext";

// Currency preference + live conversion. Amounts are stored in USD; the UI shows ONLY
// the user's preferred currency — default FCFA (XOF). Rates come from /api/rates
// (frankfurter.dev USD→EUR + the fixed 655.957 XOF/EUR peg), with a safe fallback.

export const CURRENCIES = ["XOF", "USD", "EUR"];
const FALLBACK_RATES = { USD: 1, EUR: 0.86, XOF: 564 };
const LS_KEY = "sc_currency";

const CurrencyContext = createContext({ currency: "XOF", setCurrency: () => {}, fmt: (n) => `${n}`, fmtShort: (n) => `${n}` });

const groupFr = (n, digits = 0) => n.toLocaleString("fr-FR", { maximumFractionDigits: digits });

function format(usd, currency, rates, short) {
  const n = (Number(usd) || 0) * (rates[currency] || 1);
  if (currency === "USD") {
    if (short) return n >= 1_000_000 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
    return `$${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  }
  if (currency === "EUR") {
    if (short) return n >= 1_000_000 ? `${(n / 1e6).toFixed(1).replace(".", ",")} M€` : n >= 1000 ? `${Math.round(n / 1000)} k€` : `${Math.round(n)} €`;
    return `${groupFr(n)} €`;
  }
  // XOF — FCFA
  if (short) return n >= 1_000_000_000 ? `${(n / 1e9).toFixed(1).replace(".", ",")} Md FCFA`
    : n >= 1_000_000 ? `${(n / 1e6).toFixed(n >= 10e6 ? 0 : 1).replace(".", ",")} M FCFA`
      : `${groupFr(n)} FCFA`;
  return n >= 1_000_000 ? `${(n / 1e6).toFixed(1).replace(".", ",")} M FCFA` : `${groupFr(n)} FCFA`;
}

export function CurrencyProvider({ children }) {
  const { user, setUser } = useAuth();
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesReady, setRatesReady] = useState(false);
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || "XOF"; } catch { return "XOF"; }
  });

  useEffect(() => {
    api.get("/rates")
      .then(({ data }) => { if (data?.rates?.XOF) setRates(data.rates); })
      .catch(() => { /* keep the fallback rates */ })
      .finally(() => setRatesReady(true));
  }, []);

  // The account preference wins over the device preference once known.
  useEffect(() => {
    if (user?.currency && CURRENCIES.includes(user.currency)) setCurrencyState(user.currency);
  }, [user?.currency]);

  const setCurrency = (c) => {
    if (!CURRENCIES.includes(c)) return;
    setCurrencyState(c);
    try { localStorage.setItem(LS_KEY, c); } catch { /* ignore */ }
    if (user) {
      api.put("/me", { currency: c }).then(({ data }) => setUser?.(data)).catch(() => {});
    }
  };

  const value = {
    currency,
    setCurrency,
    ratesReady,
    fmt: (usd) => format(usd, currency, rates, false),
    fmtShort: (usd) => format(usd, currency, rates, true),
    // Amounts are stored in USD, so any field the user *types into* has to
    // convert both ways. Without this, "your typical ticket" silently meant
    // "in dollars" no matter which currency the visitor had chosen.
    toUsd: (amount) => (Number(amount) || 0) / (rates[currency] || 1),
    fromUsd: (usd) => (Number(usd) || 0) * (rates[currency] || 1),
  };
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
